import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, Building2, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { engineeringServices } from '../data/mockData';

const Services = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Engineering Services',
      subtitle: 'Professional CAD & MEP Design Services with International Standards',
      process: 'Our Process',
      capabilities: 'Capabilities',
      standards: 'Standards',
      deliverables: 'Deliverables',
      getStarted: 'Get Started'
    },
    vi: {
      title: 'Dịch vụ kỹ thuật',
      subtitle: 'Dịch vụ thiết kế CAD & MEP chuyên nghiệp với tiêu chuẩn quốc tế',
      process: 'Quy trình của chúng tôi',
      capabilities: 'Khả năng',
      standards: 'Tiêu chuẩn',
      deliverables: 'Sản phẩm giao',
      getStarted: 'Bắt đầu'
    }
  };

  const t = content[language];

  const process = [
    {
      step: '01',
      title: language === 'en' ? 'Requirement Analysis' : 'Phân tích yêu cầu',
      description: language === 'en' 
        ? 'Understanding project scope, technical specifications, and deliverables'
        : 'Hiểu phạm vi dự án, thông số kỹ thuật và sản phẩm giao'
    },
    {
      step: '02',
      title: language === 'en' ? 'Preliminary Design' : 'Thiết kế sơ bộ',
      description: language === 'en'
        ? 'Initial drawings and calculations based on requirements and standards'
        : 'Bản vẽ và tính toán ban đầu dựa trên yêu cầu và tiêu chuẩn'
    },
    {
      step: '03',
      title: language === 'en' ? 'Client Review & Revisions' : 'Đánh giá khách hàng & sửa đổi',
      description: language === 'en'
        ? 'Feedback incorporation and design modifications as required'
        : 'Kết hợp phản hồi và sửa đổi thiết kế theo yêu cầu'
    },
    {
      step: '04',
      title: language === 'en' ? 'Final Delivery' : 'Giao hàng cuối cùng',
      description: language === 'en'
        ? 'Complete documentation, drawings, and technical specifications'
        : 'Tài liệu hoàn chỉnh, bản vẽ và thông số kỹ thuật'
    }
  ];

  const iconMap = {
    'drafting-compass': FileText,
    'building': Building2,
    'clipboard-check': ClipboardCheck
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#264f78] to-[#1a3a57] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-lg text-white/90">{t.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Services Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {engineeringServices.filter(service => service.id !== 'es3').map((service) => {
              const Icon = iconMap[service.icon] || FileText;
              return (
                <Card key={service.id} className="p-6 hover:shadow-xl transition-all duration-300 group">
                  <div className="w-16 h-16 bg-[#264f78]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#264f78] transition-colors duration-300">
                    <Icon className="w-8 h-8 text-[#264f78] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#264f78] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">{t.capabilities}</h4>
                    <ul className="space-y-1">
                      {service.capabilities.slice(0, 3).map((cap, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                          <CheckCircle2 className="w-3 h-3 text-[#264f78] mt-0.5 flex-shrink-0" />
                          <span>{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">{t.standards}</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.standards.map((std, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {std}
                        </span>
                      ))}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t.process}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Our systematic approach ensures quality deliverables and client satisfaction'
                : 'Cách tiếp cận có hệ thống của chúng tôi đảm bảo chất lượng sản phẩm giao và sự hài lòng của khách hàng'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#264f78]/5 to-[#adbccb]/10 rounded-lg p-6 h-full">
                  <div className="text-5xl font-bold text-[#264f78]/20 mb-4">{step.step}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-[#adbccb]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t.deliverables}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {engineeringServices.filter(service => service.id !== 'es3').map((service) => (
              <Card key={service.id} className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{service.title}</h3>
                <ul className="space-y-2">
                  {service.deliverables.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-[#264f78] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#264f78] to-[#1a3a57] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' ? 'Start Your Engineering Project' : 'Bắt đầu dự án kỹ thuật của bạn'}
          </h2>
          <p className="text-lg text-white/90 mb-8">
            {language === 'en'
              ? 'Share your project requirements and our engineering team will provide a detailed proposal and timeline.'
              : 'Chia sẻ yêu cầu dự án của bạn và nhóm kỹ sư của chúng tôi sẽ cung cấp đề xuất chi tiết và thời gian.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-quote">
              <Button className="bg-white text-[#264f78] hover:bg-gray-100 px-8 py-6 text-lg transition-all duration-300">
                {language === 'en' ? 'Request Engineering Consultation' : 'Yêu cầu tư vấn kỹ thuật'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/engineering-portfolio">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#264f78] px-8 py-6 text-lg transition-all duration-300">
                {language === 'en' ? 'View Portfolio' : 'Xem danh mục dầu tư'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
